/**
 * 换我出镜参考视频:体检 → 上传 → 转发件嗅探(0924 车 D 从 VideoRealCreatePage 拆出)。
 *
 * 与拆出前的行为差异(一期止血,契约 §3.4):
 *   · 存**原始**时长(`rawDurationSec`),不再按模型上限钳 —— 档位判定交给
 *     `assessSwapDuration`(随模型实时重算),超档就明说「切 2.5」或「重拍」;
 *   · 超过全档位上限(30s)**上传即拦**:不白传一个注定用不了的文件;
 *   · 通用体检那句「超过 15 秒…服务器只会取前 15 秒,建议剪到 5~15 秒」在这里不适用
 *     (换我出镜不截断,且剪过的视频基本被拒)—— 丢掉它,由档位判定接管;
 *   · 疑似转发/转码件(wxMeta / Lavf / 低码率)只警示(`forwardedHint`),不进 issue、不拦。
 */
import { useRef, useState } from "react";

import {
  VIDEO_MAX_PIXELS,
  VIDEO_MAX_SEC,
  checkVideoFile,
  readVideoMeta,
  type VideoIssue,
} from "@/shared/lib/videoFileCheck";
import { videoService } from "../../../api/videoService";
import { forwardedHintText, sniffForwardedVideo } from "../../../model/forwardedVideo";
import {
  SWAP_ABSOLUTE_MAX_SEC,
  SWAP_DURATION_TOLERANCE_SEC,
} from "../../../model/realPersonSwapTier";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 换我出镜专属下限(0915 api 车 A 收车提醒,VID-101):vapeur 全系 vendor 真实接受的
 * durationSec 下限是 4 秒 —— 比 `checkVideoFile` 通用体检的 2 秒更严。不改共享常量
 * (那道 2 秒闸服务的是人物页口播视频槽,另一条消费线)。
 */
export const SWAP_MIN_DURATION_SEC = 4;

export interface SwapReferenceState {
  file: File | null;
  issue: VideoIssue | null;
  checking: boolean;
  uploading: boolean;
  objectKey: string | null;
  /** 参考视频实测原始秒数(浮点,不钳)。 */
  rawDurationSec: number | null;
  /** 疑似转发件提醒(只警示)。 */
  forwardedHint: string | null;
  onPickFile: (f: File | undefined) => Promise<void>;
}

export function useSwapReference(): SwapReferenceState {
  const [file, setFile] = useState<File | null>(null);
  const [issue, setIssue] = useState<VideoIssue | null>(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [objectKey, setObjectKey] = useState<string | null>(null);
  const [rawDurationSec, setRawDurationSec] = useState<number | null>(null);
  const [forwardedHint, setForwardedHint] = useState<string | null>(null);
  // 连续换文件时,只认最后一次挑选的结果(先挑的慢返回不能覆盖后挑的)。
  const seq = useRef(0);

  const onPickFile = async (f: File | undefined) => {
    if (!f) return;
    const mine = ++seq.current;
    const live = () => seq.current === mine;
    setFile(f);
    setIssue(null);
    setObjectKey(null);
    setRawDurationSec(null);
    setForwardedHint(null);
    setChecking(true);
    try {
      const [generic, meta] = await Promise.all([checkVideoFile(f), readVideoMeta(f)]);
      if (!live()) return;
      let finalIssue: VideoIssue | null = generic;
      const dur = meta && Number.isFinite(meta.duration) ? meta.duration : null;
      // 通用体检的「超 15 秒只取前 15 秒,建议剪」警示在换我出镜不成立(见头注)——
      // 丢掉;它在通用体检里排在像素警示之前会把后者吞掉,这里补回像素那一句。
      if (generic && !generic.blocksSubmit && dur != null && dur > VIDEO_MAX_SEC) {
        finalIssue =
          meta && meta.width * meta.height > VIDEO_MAX_PIXELS
            ? {
                text: `分辨率 ${meta.width}×${meta.height} 超过 209 万像素上限(约 1080P)—— 过大可能被压缩或被拒`,
                blocksSubmit: false,
              }
            : null;
      }
      // vapeur 4 秒下限:只在通用体检没先一步拦下时叠加(通用阻断理由优先)。
      if (!finalIssue?.blocksSubmit && dur != null && dur < SWAP_MIN_DURATION_SEC) {
        finalIssue = {
          text: `时长仅 ${dur.toFixed(1)} 秒,换我出镜至少需要 ${SWAP_MIN_DURATION_SEC} 秒的参考视频 —— 换一段更长的`,
          blocksSubmit: true,
        };
      }
      setIssue(finalIssue);
      if (dur != null && dur > 0) setRawDurationSec(dur);
      setChecking(false);
      const overMax = dur != null && dur > SWAP_ABSOLUTE_MAX_SEC + SWAP_DURATION_TOLERANCE_SEC;
      // 体检阻断 / 超全档位上限:不上传(档位提示由页面按 rawDurationSec 渲染)。
      if (finalIssue?.blocksSubmit || overMax) return;
      void sniffForwardedVideo(f, dur).then((signals) => {
        if (live()) setForwardedHint(forwardedHintText(signals));
      });
      setUploading(true);
      try {
        // 复用现有视频上传通路拿 objectKey(videoService.uploadFrame,不新造)。
        const { key } = await videoService.uploadFrame(f);
        if (live()) setObjectKey(key);
      } catch (e) {
        if (live()) {
          setIssue({
            text: errorText(e, { hiddenDescription: "上传失败,请重试" }),
            blocksSubmit: true,
          });
        }
      } finally {
        if (live()) setUploading(false);
      }
    } finally {
      if (live()) setChecking(false);
    }
  };

  return {
    file,
    issue,
    checking,
    uploading,
    objectKey,
    rawDurationSec,
    forwardedHint,
    onPickFile,
  };
}
